import {type Country, InformationApi} from '@apispec/validator';
import {createApiInstance} from '@plugins/api/api';
import {getResponseObject} from '@plugins/http/httpTools';
import {computed, type ComputedRef, ref, type Ref} from 'vue';

const defaultCountries = [{
  iso: 'NL',
  iso3l: 'NLD',
  names: {
    nl: 'Nederland',
    en: 'The Netherlands',
  },
}];

export class CountriesService {
  private currentLocale: Ref<string>;
  private countries: Ref<Country[]> = ref(defaultCountries);
  private isResolving?: Promise<void>;

  constructor(currentLocale: Ref<string>) {
    this.currentLocale = currentLocale;
  }

  public getCountries(): Ref<Country[]> {
    this.resolveCountries().catch(e => console.error('Failed to load countries', e));

    return this.countries;
  }

  public getCountryNameRef(countryCode: ComputedRef<string | null> | Ref<string | null>): ComputedRef<string | null> {
    return computed(() => this.getCountryName(countryCode.value));
  }

  public getCountryName(countryCode?: string | null): string | null {
    if (!countryCode) {
      return null;
    }

    const country = this.getCountries().value.find(c => c.iso === countryCode.toUpperCase());
    if (!country) {
      return null;
    }

    return this.translateCountry(country);
  }

  public translateCountry(country: Country): string | null {
    return this.currentLocale.value in country.names
      ? (country.names[this.currentLocale.value as 'nl' | 'en'] ?? null)
      : null;
  }

  private resolveCountries(): Promise<void> {
    if (this.isResolving) {
      return this.isResolving;
    }

    this.isResolving = new Promise(resolve => {
      const informationApi = createApiInstance(InformationApi);

      getResponseObject(() => informationApi.retrieveCountries())
        .then(countryData => {
          if (countryData) {
            this.countries.value = countryData;
          }

          resolve();
        })
        .catch(e => {
          console.error('Failed to retrieve country data', e);
          resolve();
        });
    });

    return this.isResolving;
  }
}
